/**
 * Componente de grade de horários por profissional
 */

import type { PublicProvider } from "../types";
import { resolveImageUrl } from "../utils";
import { getProviderInitials } from "../utils/publicPeople";

interface ProviderTimeSlotsGridProps {
  providers: PublicProvider[];
  providerSlots: Record<string, string[]>;
  selectedProviderId: string;
  selectedTime: string;
  loadingProviderSlots: boolean;
  onSlotClick: (providerId: string, time: string) => void;
}

export function ProviderTimeSlotsGrid({
  providers,
  providerSlots,
  selectedProviderId,
  selectedTime,
  loadingProviderSlots,
  onSlotClick,
}: ProviderTimeSlotsGridProps) {
  if (providers.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-12">
      <div className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/70 sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-secondary-400" />
          <h3 className="text-lg font-bold tracking-tight text-slate-950">
            Horários por profissional
          </h3>
        </div>

        {loadingProviderSlots ? (
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-600">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
            <span>Carregando horários por profissional...</span>
          </div>
        ) : (
          <div>
            {(() => {
              const providersWithSlots = providers.filter(
                (p) => (providerSlots[p.id] || []).length > 0
              );

              if (providersWithSlots.length === 0) {
                return (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm font-semibold text-slate-600">
                    Nenhum profissional atende neste dia.
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {providersWithSlots.map((p) => {
                    const pTimes = providerSlots[p.id] || [];
                    return (
                      <div
                        key={p.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3"
                      >
                        <div className="mb-3 flex items-center gap-2">
                          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white text-[11px] font-bold text-slate-700 shadow-sm">
                            {p.imageUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={resolveImageUrl(p.imageUrl)}
                                alt={`Foto de ${p.name}`}
                                className="h-full w-full object-cover"
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              getProviderInitials(p.name)
                            )}
                          </div>
                          <div className="min-w-0 font-bold text-slate-900">
                            <span className="line-clamp-1">{p.name}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {pTimes.slice(0, 12).map((t) => (
                            <button
                              key={`${p.id}-${t}`}
                              type="button"
                              onClick={() => onSlotClick(p.id, t)}
                              className={`rounded-xl border px-3 py-2 text-sm font-bold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${
                                selectedProviderId === p.id && selectedTime === t
                                  ? "border-primary-600 bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </section>
  );
}
